import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { Plus, Save, Edit3, Trash2, Eye, EyeOff } from "lucide-react";
import BilingualField from "../BilingualField";
import ImageUploader from "../ImageUploader";
import RichEditor from "../RichEditor";

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("fr");

  const load = () => api.get("/posts").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    const payload = {
      ...editing,
      slug: editing.slug || "",
      tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : (editing.tags || []),
    };
    try {
      if (editing.id) await api.put(`/posts/${editing.id}`, payload);
      else await api.post("/posts", payload);
      setEditing(null);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/posts/${id}`);
    load();
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl">Blog</h1>
          <p className="text-[#4A3F35] mt-2">Articles publiés sur la page <strong>/blog</strong>.</p>
        </div>
        <button onClick={() => setEditing({ slug: "", title: { fr: "", en: "" }, excerpt: { fr: "", en: "" }, content: { fr: "", en: "" }, cover_url: "", category: "", tags: [], author: "Sarita", published: true, reading_time: 3 })} className="btn-primary" data-testid="add-post">
          <Plus size={16} /> Nouvel article
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-[#FDFBF7] rounded-2xl p-5 border border-[#2D2A26]/10 flex gap-4">
            {p.cover_url && <img src={p.cover_url} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs">
                {p.category && <span className="text-[#C87A5E] uppercase tracking-wider">{p.category}</span>}
                <span className={`inline-flex items-center gap-1 ${p.published ? "text-[#8A9A86]" : "text-[#4A3F35]/50"}`}>
                  {p.published ? <><Eye size={11} /> Publié</> : <><EyeOff size={11} /> Brouillon</>}
                </span>
              </div>
              <h3 className="font-serif text-xl truncate mt-1">{p.title?.fr || p.title?.en || "(sans titre)"}</h3>
              <p className="text-sm text-[#4A3F35] mt-1 line-clamp-2">{p.excerpt?.fr || ""}</p>
              <div className="text-xs text-[#4A3F35]/50 mt-2">/{p.slug}</div>
              <div className="flex gap-3 mt-2 text-sm">
                <button onClick={() => setEditing(p)} className="link-underline text-[#C87A5E] inline-flex items-center gap-1" data-testid={`edit-post-${p.id}`}><Edit3 size={12} /> Modifier</button>
                <button onClick={() => remove(p.id)} className="link-underline text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#FDFBF7] rounded-3xl p-8 w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()} data-testid="post-editor">
            <h3 className="font-serif text-2xl mb-6">{editing.id ? "Modifier l'article" : "Nouvel article"}</h3>

            <div className="grid md:grid-cols-3 gap-5 mb-5">
              <div className="md:col-span-2">
                <ImageUploader label="Image de couverture" value={editing.cover_url} onChange={(v) => setEditing({ ...editing, cover_url: v })} testid="post-cover" />
              </div>
              <div className="space-y-4">
                <Input label="Slug (URL)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} placeholder="auto-généré-si-vide" />
                <Input label="Catégorie" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
                <Input label="Tags (séparés par virgule)" value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
                <Input label="Auteur" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
                <Input label="Temps de lecture (min)" type="number" value={editing.reading_time} onChange={(v) => setEditing({ ...editing, reading_time: Number(v) })} />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="post-published" />
                  Publié
                </label>
              </div>
            </div>

            <BilingualField label="Titre" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="post-title" />
            <div className="mt-4">
              <BilingualField label="Extrait" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} type="textarea" testid="post-excerpt" />
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Contenu</label>
                <div className="flex gap-1 bg-[#F6F3EC] rounded-lg p-1">
                  {["fr", "en"].map((l) => (
                    <button key={l} type="button" onClick={() => setTab(l)} className={`px-2.5 py-0.5 rounded text-xs uppercase tracking-wider ${tab === l ? "bg-[#2D2A26] text-white" : "text-[#4A3F35]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <RichEditor value={(editing.content && editing.content[tab]) || ""} onChange={(v) => setEditing({ ...editing, content: { ...(editing.content || {}), [tab]: v } })} testid={`post-content-${tab}`} />
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={save} className="btn-primary flex-1 justify-center" data-testid="post-save"><Save size={16} /> Enregistrer</button>
              <button onClick={() => setEditing(null)} className="btn-ghost">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-[#2D2A26]/15 rounded-xl px-3 py-2 text-sm focus:border-[#C87A5E] outline-none" />
    </div>
  );
}
